import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Icon } from 'utils/icons';
import API_ENDPOINTS from 'utils/api/endpoints';

import './BudgetSearchOverlay.scss';

const SEARCH_DEBOUNCE_MS = 120;
const RESULT_LIMIT = 30;

const RESULT_TYPE_LABELS = {
  category: 'Category',
  line_item: 'Line Item',
  note: 'Note',
};

const RESULT_TYPE_ICONS = {
  category: 'CategoryIcon',
  line_item: 'ICursorIcon',
  note: 'CommentIcon',
};

const normalizeResultType = type =>
  Object.hasOwn(RESULT_TYPE_LABELS, type) ? type : 'line_item';

const isOpenSearchShortcut = event => {
  if (event.isComposing) return false;
  return (
    event.shiftKey &&
    (event.metaKey || event.ctrlKey) &&
    String(event.key || '').toLowerCase() === 'o'
  );
};

const BudgetSearchOverlay = ({ budgetId, onSelectResult }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  const closeOverlay = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    setActiveIndex(0);
    setIsLoading(false);
    setHasError(false);
  }, []);

  const openOverlay = useCallback(() => {
    if (!budgetId) return;

    setIsOpen(true);
    setQuery('');
    setResults([]);
    setActiveIndex(0);
    setHasError(false);
  }, [budgetId]);

  useEffect(() => {
    const handleGlobalKeyDown = event => {
      if (!isOpenSearchShortcut(event)) return;

      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent?.stopImmediatePropagation?.();
      openOverlay();
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [openOverlay]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const activeResult = document.getElementById(
      `budget-search-result-${activeIndex}`
    );
    activeResult?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const trimmedQuery = query.trim();
    setActiveIndex(0);

    if (!trimmedQuery) {
      requestIdRef.current += 1;
      setResults([]);
      setIsLoading(false);
      setHasError(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setHasError(false);

    const searchTimer = setTimeout(async () => {
      try {
        const response = await API_ENDPOINTS.budgets.search_budget({
          budgetId,
          query: trimmedQuery,
          limit: RESULT_LIMIT,
        });

        if (requestIdRef.current !== requestId) return;

        setResults(response?.data?.results || []);
        setHasError(false);
      } catch (error) {
        if (requestIdRef.current !== requestId) return;

        setResults([]);
        setHasError(true);
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(searchTimer);
  }, [budgetId, isOpen, query]);

  const handleResultSelect = useCallback(
    result => {
      if (!result) return;

      closeOverlay();
      onSelectResult(result);
    },
    [closeOverlay, onSelectResult]
  );

  const handleInputKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeOverlay();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index =>
        Math.min(index + 1, Math.max(results.length - 1, 0))
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleResultSelect(results[activeIndex]);
    }
  };

  if (!isOpen) return null;

  const hasQuery = query.trim().length > 0;

  return (
    <div
      className="budget-search-overlay"
      onMouseDown={event => {
        if (event.target === event.currentTarget) closeOverlay();
      }}
    >
      <div
        className="budget-search-overlay__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Budget search"
      >
        <div
          className={[
            'budget-search-overlay__input-row',
            results.length > 0 ? 'has-results' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Icon name="SearchIcon" className="budget-search-overlay__icon" />
          <input
            ref={inputRef}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Search budget"
            aria-label="Search budget"
            autoComplete="off"
            spellCheck="false"
          />
          <button
            type="button"
            className="budget-search-overlay__close"
            aria-label="Close search"
            onClick={closeOverlay}
          >
            <Icon name="CloseIcon" />
          </button>
        </div>

        <div className="budget-search-overlay__results" role="listbox">
          {hasQuery && hasError && !isLoading && (
            <div className="budget-search-overlay__empty">
              Search unavailable
            </div>
          )}

          {hasQuery && !isLoading && !hasError && results.length === 0 && (
            <div className="budget-search-overlay__empty">No matches</div>
          )}

          {results.map((result, index) => {
            const type = normalizeResultType(result.type);
            const isActive = index === activeIndex;

            return (
              <button
                key={result.id || `${type}-${index}`}
                id={`budget-search-result-${index}`}
                type="button"
                role="option"
                aria-selected={isActive}
                className={[
                  'budget-search-overlay__result',
                  isActive ? 'is-active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={event => event.preventDefault()}
                onClick={() => handleResultSelect(result)}
              >
                <span
                  className={`budget-search-overlay__badge budget-search-overlay__badge--${type}`}
                >
                  <Icon name={RESULT_TYPE_ICONS[type]} />
                  {RESULT_TYPE_LABELS[type]}
                </span>
                <span className="budget-search-overlay__result-main">
                  <span className="budget-search-overlay__result-title">
                    {result.account_number
                      ? `${result.account_number} ${result.title}`
                      : result.title}
                  </span>
                  <span className="budget-search-overlay__result-path">
                    {result.path_label}
                  </span>
                  {type === 'note' && result.excerpt && (
                    <span className="budget-search-overlay__result-excerpt">
                      {result.excerpt}
                    </span>
                  )}
                </span>
              </button>
            );
          })}

          {hasQuery && isLoading && (
            <div className="budget-search-overlay__empty">Searching...</div>
          )}
        </div>
      </div>
    </div>
  );
};

BudgetSearchOverlay.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectResult: PropTypes.func.isRequired,
};

BudgetSearchOverlay.defaultProps = {
  budgetId: null,
};

export default BudgetSearchOverlay;
